/**
 * Recurring Money Context — the store behind the Month Flow view.
 *
 * Owns exactly ONE collection, `recurring_money`: the household bills and any
 * income the business systems don't already know about.
 *
 * It also LOADS (never writes) the two collections that already describe
 * recurring business money — `repeating_invoices` and `crm_subscriptions` — so
 * the view can project all three together. Copying those into `recurring_money`
 * would have been easier and wrong: they have their own owners and editors, and
 * a copy goes stale the moment someone changes a real template.
 *
 * Subscriptions are read here rather than taken from SubscriptionsProvider so
 * this context works standalone; the provider higher up the tree is scoped to
 * the CRM pages' needs and pulls payment history we don't want on this page.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  getFirestore,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { RecurringMoneyItem } from '../types';
import { RepeatingInvoice } from '../../../types/accounting';
import { CrmSubscription } from '../../../contexts/SubscriptionsContext';

const ITEMS_COLL = 'recurring_money';
const TEMPLATES_COLL = 'repeating_invoices';
const SUBSCRIPTIONS_COLL = 'crm_subscriptions';

/** Firestore rejects `undefined`. Optional fields are simply absent on the
 *  document rather than stored as null, which keeps `x !== undefined` checks
 *  meaningful on the way back out. */
function stripUndefined<T extends Record<string, any>>(obj: T): T {
  const out: any = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v;
  }
  return out;
}

/** Coerce a stored document into the item shape, tolerating older/partial
 *  records. A document that predates a field must not crash the projection. */
function toItem(id: string, d: any): RecurringMoneyItem {
  return {
    id,
    name: String(d?.name || 'Untitled'),
    direction: d?.direction === 'in' ? 'in' : 'out',
    amount: Number(d?.amount) || 0,
    // `0` is a meaningful expectedAmount ("not due this cycle"), so this
    // preserves it and only drops genuine absence.
    ...(d?.expectedAmount === undefined || d?.expectedAmount === null
      ? {}
      : { expectedAmount: Number(d.expectedAmount) || 0 }),
    kind: d?.kind || 'monthly_spread',
    ...(d?.dayOfMonth !== undefined && d?.dayOfMonth !== null ? { dayOfMonth: Number(d.dayOfMonth) } : {}),
    ...(d?.weekday !== undefined && d?.weekday !== null ? { weekday: Number(d.weekday) } : {}),
    ...(d?.month !== undefined && d?.month !== null ? { month: Number(d.month) } : {}),
    ...(d?.anchorDate ? { anchorDate: String(d.anchorDate) } : {}),
    ...(d?.startDate ? { startDate: String(d.startDate) } : {}),
    ...(d?.endDate ? { endDate: String(d.endDate) } : {}),
    category: String(d?.category || 'expense:other'),
    scope: d?.scope === 'business' ? 'business' : 'personal',
    // Absent = on. A record written before this field existed should still
    // count towards the month.
    active: d?.active !== false,
    ...(d?.notes ? { notes: String(d.notes) } : {}),
    ...(d?.sortOrder !== undefined && d?.sortOrder !== null ? { sortOrder: Number(d.sortOrder) } : {}),
    createdBy: d?.createdBy,
    createdAt: d?.createdAt,
    updatedAt: d?.updatedAt,
  };
}

export type NewRecurringMoneyItem = Omit<RecurringMoneyItem, 'id' | 'createdAt' | 'updatedAt'>;

interface RecurringMoneyValue {
  items: RecurringMoneyItem[];
  templates: RepeatingInvoice[];
  subscriptions: CrmSubscription[];
  loading: boolean;
  error: string | null;
  /** Non-fatal: the manual items loaded but a business source didn't. The view
   *  shows a banner instead of silently under-reporting income.
   *
   *  `retryable` separates a transient failure from a permissions one. Stripe
   *  plans are admin-only (firestore.rules), so a non-admin user will ALWAYS
   *  be denied — offering them a Retry button would be a lie. */
  sourceErrors: Array<{ message: string; retryable: boolean }>;
  refresh: () => Promise<void>;
  addItem: (item: NewRecurringMoneyItem) => Promise<string>;
  updateItem: (id: string, patch: Partial<NewRecurringMoneyItem>) => Promise<void>;
  deleteItem: (id: string) => Promise<void>;
  setActive: (id: string, active: boolean) => Promise<void>;
  /** Bulk create from the import preview. Returns how many were written. */
  importItems: (items: NewRecurringMoneyItem[]) => Promise<number>;
  /** Replace an existing item wholesale — used when an import row is confirmed
   *  as an update to a duplicate rather than a new bill. */
  replaceItem: (id: string, item: NewRecurringMoneyItem) => Promise<void>;
}

const RecurringMoneyContext = createContext<RecurringMoneyValue | null>(null);

export function useRecurringMoney(): RecurringMoneyValue {
  const ctx = useContext(RecurringMoneyContext);
  if (!ctx) throw new Error('useRecurringMoney must be used inside RecurringMoneyProvider');
  return ctx;
}

/** Firestore caps a batched write at 500 operations. */
function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

export function RecurringMoneyProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<RecurringMoneyItem[]>([]);
  const [templates, setTemplates] = useState<RepeatingInvoice[]>([]);
  const [subscriptions, setSubscriptions] = useState<CrmSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sourceErrors, setSourceErrors] = useState<Array<{ message: string; retryable: boolean }>>([]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    const db = getFirestore();

    // The manual items are this page's own data — if they fail, the page has
    // nothing to show and that's a hard error. The two business sources are
    // additive: losing one should degrade the forecast with a warning, not
    // blank the screen.
    const [itemsResult, templatesResult, subsResult] = await Promise.allSettled([
      getDocs(query(collection(db, ITEMS_COLL))),
      getDocs(query(collection(db, TEMPLATES_COLL))),
      getDocs(query(collection(db, SUBSCRIPTIONS_COLL))),
    ]);

    const problems: Array<{ message: string; retryable: boolean }> = [];
    const denied = (reason: any) => reason?.code === 'permission-denied';

    if (itemsResult.status === 'fulfilled') {
      setItems(itemsResult.value.docs.map((d) => toItem(d.id, d.data())));
    } else {
      setError(itemsResult.reason?.message || 'Could not load your recurring items');
    }

    if (templatesResult.status === 'fulfilled') {
      setTemplates(
        templatesResult.value.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as RepeatingInvoice[]
      );
    } else {
      setTemplates([]);
      problems.push(
        denied(templatesResult.reason)
          ? {
              message: 'Recurring invoices and bills aren’t visible to your account, so they’re left out of this forecast.',
              retryable: false,
            }
          : { message: 'Recurring invoices and bills couldn’t be loaded — income is understated.', retryable: true }
      );
    }

    if (subsResult.status === 'fulfilled') {
      setSubscriptions(
        subsResult.value.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as CrmSubscription[]
      );
    } else {
      setSubscriptions([]);
      // Stripe plans are admin-only by rule, so this is the EXPECTED path for a
      // non-admin user rather than a fault. Say so plainly; don't imply
      // something is broken and don't offer a retry that can't succeed.
      problems.push(
        denied(subsResult.reason)
          ? {
              message: 'Stripe plans are admin-only, so plan income isn’t included in this forecast.',
              retryable: false,
            }
          : { message: 'Stripe plans couldn’t be loaded — income is understated.', retryable: true }
      );
    }

    setSourceErrors(problems);
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const addItem = useCallback(async (item: NewRecurringMoneyItem): Promise<string> => {
    const db = getFirestore();
    const uid = getAuth().currentUser?.uid;
    const ref = doc(collection(db, ITEMS_COLL));
    const payload = stripUndefined({
      ...item,
      createdBy: uid,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    await setDoc(ref, payload);
    // Optimistic local insert: the page recomputes the whole month from this
    // array, so waiting for a refetch would make every edit feel laggy.
    setItems((prev) => [...prev, toItem(ref.id, { ...item, createdBy: uid })]);
    return ref.id;
  }, []);

  const updateItem = useCallback(async (id: string, patch: Partial<NewRecurringMoneyItem>) => {
    const db = getFirestore();
    await updateDoc(doc(db, ITEMS_COLL, id), stripUndefined({ ...patch, updatedAt: serverTimestamp() }));
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  }, []);

  const replaceItem = useCallback(async (id: string, item: NewRecurringMoneyItem) => {
    const db = getFirestore();
    const uid = getAuth().currentUser?.uid;
    // setDoc without merge, so fields the new shape doesn't have (a dayOfMonth
    // left behind by a monthly item that's now weekly) are actually removed
    // rather than lingering to confuse the projector.
    await setDoc(
      doc(db, ITEMS_COLL, id),
      stripUndefined({ ...item, createdBy: uid, updatedAt: serverTimestamp() })
    );
    setItems((prev) => prev.map((it) => (it.id === id ? toItem(id, { ...item, createdBy: uid }) : it)));
  }, []);

  const deleteItem = useCallback(async (id: string) => {
    const db = getFirestore();
    await deleteDoc(doc(db, ITEMS_COLL, id));
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  const setActive = useCallback(
    async (id: string, active: boolean) => {
      await updateItem(id, { active });
    },
    [updateItem]
  );

  const importItems = useCallback(async (incoming: NewRecurringMoneyItem[]): Promise<number> => {
    if (incoming.length === 0) return 0;
    const db = getFirestore();
    const uid = getAuth().currentUser?.uid;
    const created: RecurringMoneyItem[] = [];

    for (const group of chunk(incoming, 450)) {
      const batch = writeBatch(db);
      for (const item of group) {
        const ref = doc(collection(db, ITEMS_COLL));
        batch.set(
          ref,
          stripUndefined({
            ...item,
            createdBy: uid,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          })
        );
        created.push(toItem(ref.id, { ...item, createdBy: uid }));
      }
      await batch.commit();
    }

    setItems((prev) => [...prev, ...created]);
    return created.length;
  }, []);

  const value = useMemo<RecurringMoneyValue>(
    () => ({
      items,
      templates,
      subscriptions,
      loading,
      error,
      sourceErrors,
      refresh,
      addItem,
      updateItem,
      replaceItem,
      deleteItem,
      setActive,
      importItems,
    }),
    [
      items, templates, subscriptions, loading, error, sourceErrors,
      refresh, addItem, updateItem, replaceItem, deleteItem, setActive, importItems,
    ]
  );

  return <RecurringMoneyContext.Provider value={value}>{children}</RecurringMoneyContext.Provider>;
}
